import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Zalabya | Our Story & Mission</title>
        <meta name="description" content="Learn about Zalabya's journey bringing authentic Egyptian desserts to Mississauga. Our passion for traditional recipes and quality ingredients." />
      </Helmet>

      <div className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold text-gradient mb-6">Our Story</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Bringing the authentic taste of Egyptian desserts to the heart of Mississauga
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Story Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-yellow-400 mb-6">The Zalabya Journey</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Zalabya began with a simple mission: to share the incredible flavors of traditional Egyptian desserts 
                  with our Canadian community. Our name comes from the Arabic word for these delightful fried dough balls, 
                  known in Egypt as "Loukomat il Qadi" or "Judge's Bites."
                </p>
                <p>
                  What started as a passion for preserving authentic recipes has grown into Mississauga's premier 
                  destination for Egyptian desserts. We've stayed true to traditional methods while embracing modern 
                  innovations like our viral Dubai chocolate creations.
                </p>
                <p>
                  Every batch of zalabya is made fresh throughout the day, ensuring that crispy exterior and soft, 
                  fluffy interior that has made us a favorite among dessert lovers across the GTA.
                </p>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://i.ibb.co/0y0WmmwB/Inside.jpg"
                alt="Inside Zalabya Restaurant"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>

          {/* Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gradient text-center mb-12">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="card p-8 text-center">
                <div className="text-5xl mb-4">🥇</div>
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Quality First</h3>
                <p className="text-gray-300">
                  We use only the finest ingredients and never compromise on quality. 
                  Fresh oil daily and premium toppings ensure every bite is perfect.
                </p>
              </div>
              <div className="card p-8 text-center">
                <div className="text-5xl mb-4">🏺</div>
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Authentic Tradition</h3>
                <p className="text-gray-300">
                  Our recipes honor centuries-old Egyptian traditions while embracing 
                  modern tastes and dietary preferences.
                </p>
              </div>
              <div className="card p-8 text-center">
                <div className="text-5xl mb-4">❤️</div>
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Community Love</h3>
                <p className="text-gray-300">
                  We're more than a dessert shop - we're part of the Mississauga community, 
                  creating sweet memories for families and friends.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Restaurant Images */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gradient text-center mb-12">Visit Our Location</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                'https://i.ibb.co/4R10rLTR/inside-image-1.jpg',
                'https://i.ibb.co/s43Dqcj/inside-image-2.jpg',
                'https://i.ibb.co/70K1S7F/inside-image-3.jpg',
                'https://i.ibb.co/bRX9hXBz/inside-image-4.jpg'
              ].map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="overflow-hidden rounded-lg"
                >
                  <img
                    src={image}
                    alt={`Restaurant Interior ${index + 1}`}
                    className="w-full h-48 object-cover hover:scale-110 transition-transform duration-300"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mt-16"
          >
            <h2 className="text-3xl font-bold text-yellow-400 mb-6">Experience the Difference</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who have made Zalabya their go-to dessert destination
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary">
                Visit Us Today
              </a>
              <a href="/menu" className="btn-secondary">
                View Our Menu
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default About;